// Wave 20.14 — Unified feed dashboard.
//
// Route: /feed (also becomes the default landing after login). Three
// tabs (For you / Following / Mine) drive scope=... on GET /feed.
// Feed items:
//   - activity: session_summary row (indoor aero OR outdoor ride/run)
//   - peak_drop: peak from someone I follow
//   - inbox_drop: drop sent to me directly (For-You only)
// Each renders as a card with the right icon + stat shape.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import DirectionsBikeIcon from '@mui/icons-material/DirectionsBike';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BoltIcon from '@mui/icons-material/Bolt';
import InboxIcon from '@mui/icons-material/Inbox';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import CloudIcon from '@mui/icons-material/Cloud';
import SpeedIcon from '@mui/icons-material/Speed';
import DynamicFeedIcon from '@mui/icons-material/DynamicFeed';
import LinkIcon from '@mui/icons-material/Link';
import DropsSurface from './components/DropsSurface';
import { Link as RouterLink } from 'react-router-dom';
import PageLayout from './components/PageLayout';
import LiveFriends from './components/LiveFriends';
import DropInbox from './components/DropInbox';
import WeatherPanel from './components/WeatherPanel';
import { WindStreakGlyph } from './components/cheerGlyphs';
import {
  fmtDistance as fmtDistanceUnits,
  fmtElevation as fmtElevationUnits,
  fmtPace as fmtPaceUnits,
  fmtRelativeWind,
  fmtSpeed as fmtSpeedUnits,
  useUnitSystem,
} from './services/useUnitSystem';
import AeroPositionsPanel from './components/AeroPositionsPanel';
import { colors } from './theme';
import { useFeatures } from './services/features';

// Rapha is a store-embedded aero-fit deployment; drops are a
// commercial-only feature, so filter drop items out of the feed here.
// IS_RAPHA constant retired — every check moved to the useFeatures()
// hook inside the component so features stay explicit.

const API = process.env.REACT_APP_API || 'https://api.aero.chat';
const TOKEN_KEY = process.env.REACT_APP_TOKEN_KEY || 'aero.chat_token';

type Scope = 'for_you' | 'following' | 'me';
type Surface = 'feed' | 'drops' | 'weather' | 'aero';

type Actor = { id: number; username: string | null; displayName: string };

type FeedItem =
  | {
      kind: 'activity';
      id: string;
      when: string;
      actor: Actor;
      activity: {
        type: 'indoor_aero' | 'outdoor_cycling' | 'outdoor_running';
        startedAt: string;
        endedAt: string | null;
        title?: string | null;
        description?: string | null;
        durationSec: number | null;
        distanceM: number | null;
        elevationGainM: number | null;
        avgSpeedMps: number | null;
        bestCda: number | null;
        bestPosition: string | null;
        visibility: 'public' | 'followers' | 'private';
        avgHrBpm?: number | null;
        maxHrBpm?: number | null;
        avgPowerW?: number | null;
        maxPowerW?: number | null;
        polyline?: Array<{ lat: number; lng: number } | null> | null;
        privacyZones?: Array<{ lat: number; lng: number; radiusM: number }>;
        avgHeadwindMps?: number | null;
        positions?: Array<{
          class: string;
          cda: number | null;
          durationSec: number;
        }> | null;
        bestSegments?: {
          head_pct: number;
          torso_pct: number;
          arms_pct: number;
          legs_pct: number;
        } | null;
      };
    }
  | {
      kind: 'peak_drop';
      id: string;
      when: string;
      actor: Actor;
      peak: { kind: string; value: number; unit: string; contextLabel: string | null };
      token: string;
    }
  | {
      kind: 'inbox_drop';
      id: string;
      when: string;
      actor: Actor;
      dropType: 'activity' | 'peak';
      token: string;
      seenAt: string | null;
    };

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.round(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

export default function Feed() {
  const features = useFeatures();
  const [scope, setScope] = useState<Scope>('for_you');
  const [items, setItems] = useState<FeedItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Wave 20.29 — Below `md` (desktop), the three side surfaces
  // (weather · feed · aero positions) can't fit side by side; the
  // user asked NOT to stack, so we switch to a single-surface
  // tab picker that swaps which panel is visible.
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const [surface, setSurface] = useState<Surface>('feed');

  const load = useCallback(async (s: Scope) => {
    setError(null);
    setItems(null);
    try {
      const jwt = localStorage.getItem(TOKEN_KEY);
      if (!jwt) throw new Error('not signed in');
      const res = await fetch(`${API}/feed?scope=${s}&segments=1`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const rows: FeedItem[] = await res.json();
      // Only show items the tier supports — Rapha strips peak_drop
       // + inbox_drop rows since it doesn't have drops at all.
      const filtered = features.drops
        ? rows
        : rows.filter((r) => r.kind === 'activity');
      setItems(filtered);
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    }
  }, [features.drops]);

  useEffect(() => {
    load(scope);
  }, [scope, load]);

  // Feed column body — everything that's specific to the feed
  // itself (scope tabs, drop inbox, loading state, item list).
  const feedBody = (
    <>
      <Tabs
        value={scope}
        onChange={(_, v) => setScope(v as Scope)}
        sx={{
          mb: 3,
          borderBottom: `1px solid ${colors.border}`,
          '& .MuiTab-root': { color: colors.textMuted, fontWeight: 700, textTransform: 'none' },
          '& .Mui-selected': { color: `${colors.brand} !important` },
          '& .MuiTabs-indicator': { backgroundColor: colors.brand },
        }}
      >
        <Tab value="for_you" label="For you" />
        {features.socialFeed && <Tab value="following" label="Following" />}
        <Tab value="me" label="Mine" />
      </Tabs>
      {error && (
        <Box sx={{ p: 3, textAlign: 'center', color: colors.textMuted }}>{error}</Box>
      )}
      {items === null && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress sx={{ color: colors.brand }} />
        </Box>
      )}
      {items && items.length === 0 && (
        <Box
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: colors.surface,
            border: `1px solid ${colors.border}`,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontWeight: 800, mb: 1 }}>
            {scope === 'me'
              ? 'No sessions yet'
              : scope === 'following'
              ? 'No sessions from people you follow'
              : 'Nothing here yet'}
          </Typography>
          <Typography sx={{ color: colors.textMuted, mb: 3 }}>
            {scope === 'me'
              ? features.mobileAppPromo
                ? "Record your first ride or run in the mobile app, or start an aero session on desktop."
                : 'Start an aero session to see it here.'
              : 'Find users + follow them to see their sessions and peaks here.'}
          </Typography>
          {scope !== 'me' && features.discoverPeople && (
            <Button
              component={RouterLink}
              to="/discover"
              variant="contained"
              sx={{ backgroundColor: colors.brand, color: colors.textInverse, fontWeight: 800 }}
            >
              Discover people to follow
            </Button>
          )}
        </Box>
      )}
      {features.socialFeed && (scope === 'for_you' || scope === 'following') && <LiveFriends />}
      {features.drops && scope === 'for_you' && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
            Sent to you
          </Typography>
          <Box sx={{ mt: 1 }}>
            <DropInbox />
          </Box>
        </Box>
      )}
      {items && items.length > 0 && (
        <Stack spacing={1.5}>
          {items.map((it) => (
            <FeedCard key={`${it.kind}-${it.id}`} item={it} />
          ))}
        </Stack>
      )}
    </>
  );

  const header = (
    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
      <Box>
        <Typography variant="overline" sx={{ color: colors.brand, letterSpacing: '0.2em' }}>
          Feed
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>
          aero.chat
        </Typography>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button
          component={RouterLink}
          to="/import"
          variant="outlined"
          sx={{ color: colors.brand, borderColor: colors.brand, fontWeight: 700 }}
        >
          Manual upload
        </Button>
        {features.discoverPeople && (
          <Button
            component={RouterLink}
            to="/discover"
            variant="outlined"
            startIcon={<PersonSearchIcon />}
            sx={{ color: colors.brand, borderColor: colors.brand, fontWeight: 700 }}
          >
            Discover people
          </Button>
        )}
      </Stack>
    </Stack>
  );

  // Desktop: three columns side by side. Center column keeps the
  // existing feed content width so cards look identical. Side
  // panels sit sticky-ish (viewport-relative height so they scroll
  // their own content, not the page).
  if (isDesktop) {
    return (
      <PageLayout maxWidth="xl">
        {header}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr minmax(0, 640px) 1fr',
            gap: 3,
            alignItems: 'start',
          }}
        >
          <Box sx={{ position: 'sticky', top: 88, height: 'calc(100vh - 120px)' }}>
            <WeatherPanel height="calc(100vh - 120px)" />
          </Box>
          <Box sx={{ minWidth: 0 }}>{feedBody}</Box>
          <Box sx={{ position: 'sticky', top: 88, height: 'calc(100vh - 120px)' }}>
            <AeroPositionsPanel />
          </Box>
        </Box>
      </PageLayout>
    );
  }

  // Mobile / narrow: single surface picker at top. User asked
  // explicitly NOT to stack, so exactly one of the three panels
  // is visible at a time.
  return (
    <PageLayout maxWidth="md">
      {header}
      <Tabs
        value={surface}
        onChange={(_, v) => setSurface(v as Surface)}
        variant="fullWidth"
        sx={{
          mb: 2,
          borderBottom: `1px solid ${colors.border}`,
          '& .MuiTab-root': { color: colors.textMuted, fontWeight: 700, textTransform: 'none', minHeight: 44 },
          '& .Mui-selected': { color: `${colors.brand} !important` },
          '& .MuiTabs-indicator': { backgroundColor: colors.brand },
        }}
      >
        <Tab value="feed" icon={<DynamicFeedIcon fontSize="small" />} iconPosition="start" label="Feed" />
        <Tab value="drops" icon={<LinkIcon fontSize="small" />} iconPosition="start" label="Drops" />
        <Tab value="weather" icon={<CloudIcon fontSize="small" />} iconPosition="start" label="Weather" />
        <Tab value="aero" icon={<SpeedIcon fontSize="small" />} iconPosition="start" label="Aero" />
      </Tabs>
      {surface === 'feed' && feedBody}
      {/* Wave 20.49ae — Drops surface, web parity with mobile.
          for_you / following / mine scope tabs; mine has a
          "Show expired" toggle. */}
      {surface === 'drops' && <DropsSurface />}
      {/* Wave 20.49ct — Mobile: no height cap. WeatherPanel with
          height="auto" flows naturally; outer PageLayout scrolls. */}
      {surface === 'weather' && <WeatherPanel />}
      {surface === 'aero' && (
        <Box sx={{ height: 'min(70vh, 640px)' }}>
          <AeroPositionsPanel />
        </Box>
      )}
    </PageLayout>
  );
}

// ---------- Feed card variants ----------

function FeedCard({ item }: { item: FeedItem }) {
  if (item.kind === 'activity') return <ActivityCard item={item} />;
  if (item.kind === 'peak_drop') return <PeakDropCard item={item} />;
  return <InboxDropCard item={item} />;
}

function CardShell({
  icon,
  actor,
  when,
  chip,
  children,
  href,
}: {
  icon: React.ReactNode;
  actor: Actor;
  when: string;
  chip?: React.ReactNode;
  children: React.ReactNode;
  href?: string;
}) {
  const content = (
    <Box
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        display: 'flex',
        gap: 2,
        cursor: href ? 'pointer' : 'default',
        transition: 'border-color 120ms',
        '&:hover': href ? { borderColor: colors.brand } : {},
      }}
    >
      <Box sx={{ pt: 0.5 }}>{icon}</Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
          {actor.username ? (
            <RouterLink
              to={`/u/${actor.username}`}
              style={{ textDecoration: 'none', color: 'inherit' }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography sx={{ fontWeight: 800, color: colors.text }}>{actor.displayName}</Typography>
            </RouterLink>
          ) : (
            <Typography sx={{ fontWeight: 800, color: colors.text }}>{actor.displayName}</Typography>
          )}
          {actor.username && (
            <Typography sx={{ color: colors.textMuted, fontSize: '0.8rem' }}>@{actor.username}</Typography>
          )}
          <Typography sx={{ color: colors.textMuted, fontSize: '0.75rem' }}>· {relative(when)}</Typography>
          {chip}
        </Stack>
        <Box sx={{ mt: 0.5 }}>{children}</Box>
      </Box>
    </Box>
  );
  if (href) {
    return (
      <RouterLink to={href} style={{ textDecoration: 'none', color: 'inherit' }}>
        {content}
      </RouterLink>
    );
  }
  return content;
}

function ActivityCard({ item }: { item: Extract<FeedItem, { kind: 'activity' }> }) {
  const { activity, actor, when } = item;
  const units = useUnitSystem();
  const Icon =
    activity.type === 'outdoor_running'
      ? DirectionsRunIcon
      : activity.type === 'outdoor_cycling'
      ? DirectionsBikeIcon
      : CameraAltIcon;
  // Wave 20.40 — everything is a session. Both indoor and outdoor
  // detail views live under /sessions/:id via SessionRouter.
  const href = `/sessions/${item.id}`;
  const isOutdoor =
    activity.type === 'outdoor_cycling' || activity.type === 'outdoor_running';
  const isAero = activity.type === 'indoor_aero';
  return (
    <CardShell
      icon={<Icon sx={{ color: colors.brand, fontSize: 28 }} />}
      actor={actor}
      when={when}
      href={href}
      chip={
        activity.visibility === 'followers' ? (
          <Chip
            label="FRIENDS"
            size="small"
            sx={{ backgroundColor: `${colors.brand}22`, color: colors.brand, fontWeight: 800, height: 18, fontSize: '0.6rem' }}
          />
        ) : null
      }
    >
      <Typography sx={{ color: colors.text, fontWeight: 700 }}>
        {activity.title ||
          (activity.type === 'indoor_aero' ? 'Aero session' : activity.type === 'outdoor_running' ? 'Run' : 'Ride')}
      </Typography>
      {activity.description && (
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem', mt: 0.25, whiteSpace: 'pre-wrap', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
          {activity.description}
        </Typography>
      )}

      {/* Wave 20.25 — outdoor route sketch. Cheap SVG polyline drawn
          from a decimated GPS trace so the tile shows shape without
          pulling map tiles.
          Wave 20.42 — layered: absolute-positioned wind pill in the
          top-right so the rider can see at a glance whether they
          were fighting the wind or riding with it. */}
      {isOutdoor && activity.polyline && activity.polyline.length >= 2 && (
        <Box sx={{ mt: 1, position: 'relative' }}>
          {/* Wave 20.49bi — No zone circles on feed cards
              (visual clutter across every row). Session detail
              is where the owner verifies the mask. */}
          <RouteSketch points={activity.polyline} height={220} />
          {activity.avgHeadwindMps != null && (() => {
            const w = fmtRelativeWind(activity.avgHeadwindMps, units);
            const bg =
              w.kind === 'head'
                ? 'rgba(224,82,82,0.92)'
                : w.kind === 'tail'
                ? 'rgba(74,222,128,0.92)'
                : 'rgba(30,30,38,0.92)';
            const fg = w.kind === 'tail' ? '#0a1004' : '#ffffff';
            return (
              <Box
                sx={{
                  position: 'absolute',
                  top: 10,
                  right: 10,
                  px: 1.1,
                  py: 0.4,
                  borderRadius: 999,
                  backgroundColor: bg,
                  color: fg,
                  fontSize: '0.7rem',
                  fontWeight: 800,
                  letterSpacing: '0.06em',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
                  pointerEvents: 'none',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                <WindStreakGlyph size={14} />
                {w.label}
              </Box>
            );
          })()}
        </Box>
      )}

      <Stack direction="row" spacing={3} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 1 }}>
        {activity.distanceM != null && activity.distanceM > 0 && (
          <Metric label="Distance" value={fmtDistance(activity.distanceM, units)} />
        )}
        {activity.durationSec != null && activity.durationSec > 0 && (
          <Metric label="Time" value={fmtDuration(activity.durationSec)} />
        )}
        {activity.avgSpeedMps != null && activity.avgSpeedMps > 0 && (
          <Metric
            label={activity.type === 'outdoor_running' ? 'Pace' : 'Avg speed'}
            value={activity.type === 'outdoor_running' ? fmtPace(activity.avgSpeedMps, units) : fmtSpeed(activity.avgSpeedMps, units)}
          />
        )}
        {activity.elevationGainM != null && activity.elevationGainM > 0 && (
          <Metric label="Elev gain" value={fmtElevationUnits(activity.elevationGainM, units)} />
        )}
        {activity.avgHrBpm != null && activity.avgHrBpm > 0 && (
          <Metric label="Avg HR" value={`${Math.round(activity.avgHrBpm)} bpm`} />
        )}
        {activity.avgPowerW != null && activity.avgPowerW > 0 && (
          <Metric label="Avg power" value={`${Math.round(activity.avgPowerW)} W`} />
        )}
        {activity.bestCda != null && (
          <Metric label="Best CdA" value={activity.bestCda.toFixed(3)} />
        )}
        {activity.bestPosition && (
          <Metric label="Position" value={activity.bestPosition} />
        )}
      </Stack>

      {/* Wave 20.25 — aero-only body breakdown + top positions. */}
      {isAero && activity.bestSegments && (
        <Box sx={{ mt: 1.5 }}>
          <SegmentBars segments={activity.bestSegments} />
        </Box>
      )}
      {isAero && activity.positions && activity.positions.length > 0 && (
        <Stack direction="row" spacing={0.75} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
          {activity.positions.map((p) => (
            <Chip
              key={p.class}
              label={`${p.class} · ${fmtHms(p.durationSec)}${p.cda != null ? ` · ${p.cda.toFixed(3)}` : ''}`}
              size="small"
              sx={{
                backgroundColor: colors.surface,
                color: colors.text,
                border: `1px solid ${colors.border}`,
                fontSize: '0.65rem',
                fontWeight: 700,
                height: 20,
              }}
            />
          ))}
        </Stack>
      )}
    </CardShell>
  );
}

// Wave 20.37 — Route sketch on top of a real map. Uses the Mapbox
// Static Images API — one HTTP GET per card yields a rendered PNG
// with the polyline drawn on the dark-v11 basemap in the right
// projection. Cheap on the client (no map runtime, one <img> per
// card, browser cache handles repeats) and much more legible than
// the previous SVG-only polyline floating on a coloured background.
const MAPBOX_TOKEN =
  process.env.REACT_APP_MAPBOX_TOKEN ||
  process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
  'pk.eyJ1IjoiYWVyby1jaGF0IiwiYSI6ImNtY3J4bnp0MTBjeTYybm42bzVjbWtsdmkifQ.rbgswhST93lMRY-CzQZ05Q';

/** Encode a polyline in Google's polyline5 format (precision 5).
 *  Format: https://developers.google.com/maps/documentation/utilities/polylinealgorithm
 *  Mapbox Static Images API accepts these as `path-<w>+<hex>-<opacity>(<encoded>)`. */
function encodePolyline(points: Array<{ lat: number; lng: number }>): string {
  let lastLat = 0;
  let lastLng = 0;
  let out = '';
  const encode = (n: number): string => {
    let v = n < 0 ? ~(n << 1) : n << 1;
    let s = '';
    while (v >= 0x20) {
      s += String.fromCharCode((0x20 | (v & 0x1f)) + 63);
      v >>>= 5;
    }
    s += String.fromCharCode(v + 63);
    return s;
  };
  for (const p of points) {
    const lat = Math.round(p.lat * 1e5);
    const lng = Math.round(p.lng * 1e5);
    out += encode(lat - lastLat);
    out += encode(lng - lastLng);
    lastLat = lat;
    lastLng = lng;
  }
  return out;
}

/** Decimate a polyline down to at most `maxPoints` samples so the
 *  Mapbox URL stays under practical length limits (~8 KB). Uniform
 *  stride — good enough for a visual sketch. */
function decimate<T>(pts: T[], maxPoints: number): T[] {
  if (pts.length <= maxPoints) return pts;
  const step = Math.ceil(pts.length / maxPoints);
  const out: T[] = [];
  for (let i = 0; i < pts.length; i += step) out.push(pts[i]);
  if (out[out.length - 1] !== pts[pts.length - 1]) out.push(pts[pts.length - 1]);
  return out;
}

// Wave 20.49bh — Approximates a circle around (lat, lng) with
// radius r (metres) as an N-vertex ring returned closed.
function circleRing(
  lat: number,
  lng: number,
  radiusM: number,
  n: number,
): Array<{ lat: number; lng: number }> {
  const R = 6378137;
  const dLat = (radiusM / R) * (180 / Math.PI);
  const dLng = (radiusM / (R * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);
  const out: Array<{ lat: number; lng: number }> = [];
  for (let i = 0; i < n; i++) {
    const t = (i / n) * 2 * Math.PI;
    out.push({
      lat: lat + dLat * Math.sin(t),
      lng: lng + dLng * Math.cos(t),
    });
  }
  out.push(out[0]);
  return out;
}

function RouteSketch({
  points,
  height,
  privacyZones,
}: {
  points: Array<{ lat: number; lng: number } | null>;
  height: number;
  privacyZones?: Array<{ lat: number; lng: number; radiusM: number }>;
}) {
  if (points.filter((p) => p != null).length < 2) return null;
  const strokeHex = (colors.brand || '#6AFF45').replace('#', '').toLowerCase();
  const segments: Array<Array<{ lat: number; lng: number }>> = [[]];
  for (const p of points) {
    if (p === null) {
      if (segments[segments.length - 1].length > 0) segments.push([]);
    } else {
      segments[segments.length - 1].push(p);
    }
  }
  const routePaths = segments
    .filter((s) => s.length >= 2)
    .map((s) => {
      const encoded = encodePolyline(decimate(s, 200));
      return `path-3+${strokeHex}-0.9(${encodeURIComponent(encoded)})`;
    });
  // Wave 20.49bh — Overlay each privacy zone as a translucent
  // red circle so the owner can see WHERE their zones are
  // masking. Small zones show as a sub-pixel gap in the polyline
  // otherwise.
  const zonePaths = (privacyZones ?? []).map((z) => {
    const ring = circleRing(z.lat, z.lng, z.radiusM, 24);
    const encoded = encodePolyline(ring);
    return `path-2+ff5252-0.9+ff5252-0.22(${encodeURIComponent(encoded)})`;
  });
  const paths = [...routePaths, ...zonePaths].join(',');
  if (!paths) return null;
  const url =
    `https://api.mapbox.com/styles/v1/mapbox/dark-v11/static/` +
    `${paths}/auto/1280x720@2x` +
    `?access_token=${MAPBOX_TOKEN}&padding=80&logo=false&attribution=false`;
  return (
    <Box
      sx={{
        width: '100%',
        // Fixed 16:9 aspect ratio matches the requested image so a
        // portrait-phone card doesn't get left/right black bars. On
        // desktop the aspect ratio takes over from the old fixed
        // pixel height, which was landscape-tuned.
        aspectRatio: '16 / 9',
        // Preserve the previous height as a cap so tall (portrait)
        // desktop layouts don't explode vertically.
        maxHeight: height,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
        backgroundColor: '#0e0e12',
        backgroundImage: `url("${url}")`,
        // `cover` fills the container fully — no letterbox at any
        // container aspect. Padding on the Mapbox request keeps the
        // polyline away from the edges so cover-cropping never
        // trims the route.
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    />
  );
}

function SegmentBars({
  segments,
}: {
  segments: { head_pct: number; torso_pct: number; arms_pct: number; legs_pct: number };
}) {
  const rows: Array<{ label: string; pct: number }> = [
    { label: 'Head', pct: segments.head_pct },
    { label: 'Torso', pct: segments.torso_pct },
    { label: 'Arms', pct: segments.arms_pct },
    { label: 'Legs', pct: segments.legs_pct },
  ];
  return (
    <Stack spacing={0.5}>
      {rows.map((r) => (
        <Stack key={r.label} direction="row" spacing={1} alignItems="center">
          <Typography sx={{ color: colors.textMuted, fontSize: '0.7rem', width: 40 }}>
            {r.label}
          </Typography>
          <Box sx={{ flex: 1, height: 4, borderRadius: 2, backgroundColor: colors.bg, overflow: 'hidden' }}>
            <Box
              sx={{
                width: `${Math.max(0, Math.min(100, r.pct))}%`,
                height: '100%',
                backgroundColor: colors.brand,
              }}
            />
          </Box>
          <Typography sx={{ color: colors.brand, fontSize: '0.7rem', fontFamily: '"JetBrains Mono", monospace', width: 32, textAlign: 'right' }}>
            {r.pct.toFixed(0)}%
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
}

function fmtHms(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m === 0) return `${s}s`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function PeakDropCard({ item }: { item: Extract<FeedItem, { kind: 'peak_drop' }> }) {
  const units = useUnitSystem();
  return (
    <CardShell
      icon={<BoltIcon sx={{ color: colors.brand, fontSize: 28 }} />}
      actor={item.actor}
      when={item.when}
      href={`/peak/${item.token}`}
      chip={
        <Chip
          label="PEAK"
          size="small"
          sx={{ backgroundColor: colors.brand, color: colors.textInverse, fontWeight: 800, height: 18, fontSize: '0.6rem' }}
        />
      }
    >
      <Typography sx={{ color: colors.text, fontWeight: 700 }}>
        {kindLabel(item.peak.kind)} · {fmtPeak(item.peak.value, item.peak.unit, units, item.peak.kind)}
      </Typography>
      {item.peak.contextLabel && (
        <Typography sx={{ color: colors.textMuted, fontSize: '0.85rem' }}>{item.peak.contextLabel}</Typography>
      )}
    </CardShell>
  );
}

function InboxDropCard({ item }: { item: Extract<FeedItem, { kind: 'inbox_drop' }> }) {
  const href = item.dropType === 'peak' ? `/peak/${item.token}` : `/shared/${item.token}`;
  return (
    <CardShell
      icon={<InboxIcon sx={{ color: colors.brand, fontSize: 28 }} />}
      actor={item.actor}
      when={item.when}
      href={href}
      chip={
        !item.seenAt ? (
          <Chip
            label="NEW"
            size="small"
            sx={{ backgroundColor: colors.brand, color: colors.textInverse, fontWeight: 800, height: 18, fontSize: '0.6rem' }}
          />
        ) : null
      }
    >
      <Typography sx={{ color: colors.text, fontWeight: 700 }}>
        Sent you {item.dropType === 'peak' ? 'a peak' : 'a session drop'}
      </Typography>
    </CardShell>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography
        sx={{
          fontSize: '0.6rem',
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: colors.textMuted,
          fontWeight: 700,
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, fontFamily: '"JetBrains Mono", monospace' }}>
        {value}
      </Typography>
    </Box>
  );
}

function fmtDuration(sec: number): string {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${m}m`;
}
// Local formatters kept as thin wrappers around the unit-aware
// helpers so all callers below can keep passing raw numbers and
// let the current preference flow through automatically. Marked
// impure via the units-hook read at the call site (see components).
type UnitsArg = 'metric' | 'imperial';
function fmtDistance(m: number, u: UnitsArg): string {
  return fmtDistanceUnits(m, u);
}
function fmtSpeed(mps: number, u: UnitsArg): string {
  return fmtSpeedUnits(mps, u);
}
function fmtPace(mps: number, u: UnitsArg): string {
  return fmtPaceUnits(mps, u);
}
const PRETTY_TARGETS: Record<string, string> = {
  '5s': '5s', '30s': '30s',
  '1min': '1min', '5min': '5min', '20min': '20min', '60min': '60min',
  '1mi': '1mi', '5k': '5K', '10k': '10K', '40k': '40K',
  'half': 'Half Marathon', 'full': 'Marathon',
};
function kindLabel(kind: string): string {
  // Wave 20.46 — per-metric peaks.
  if (kind === 'peak_whole') return 'Entire session';
  if (kind === 'peak_pace') return 'Peak Pace';
  if (kind === 'peak_speed') return 'Peak Speed';
  if (kind === 'peak_power') return 'Peak Power';
  // Wave 20.49ag — cycling default (Grade-Adj Speed) since peak
  // drops in the feed usually come from rides. Running peaks
  // fall into peak_pace* which read as "Peak Pace" above.
  if (kind === 'peak_gap') return 'Peak Grade-Adj Speed';
  if (kind === 'peak_vam') return 'Peak Climb';
  if (kind === 'peak_grade_adj_speed') return 'Peak Speed';
  if (kind === 'peak_grade_adj_pace') return 'Peak Pace';
  const p = kind.split('_');
  const target = (t: string | undefined): string =>
    t ? (PRETTY_TARGETS[t] ?? t) : '';
  if (p[0] === 'peak' && p[1] === 'power' && p[2]) return `Best ${target(p[2])} Power`;
  if (p[0] === 'peak' && p[1] === 'speed' && p[2]) return `Fastest ${target(p[2])}`;
  if (p[0] === 'peak' && p[1] === 'pace' && p[2]) return `Fastest ${target(p[2])}`;
  return kind;
}
// Wave 20.49af — running-only pace kinds. Everything else with
// sec_per_m renders as speed (mph/kph) per rider preference
// "cycling should be mph/kph, running should be min/mi." Feed
// payload's peak_drop doesn't carry activityType, so we
// disambiguate by kind: peak_pace* + session_*_pace +
// summary_*_pace are running; anything else with sec_per_m
// (peak_gap on a cycling drop) formats as speed.
function isRunPaceKindForFeed(kind: string): boolean {
  if (kind === 'peak_pace' || kind.startsWith('peak_pace_')) return true;
  if (kind === 'session_avg_pace' || kind === 'summary_avg_pace') return true;
  if (kind === 'session_best_pace' || kind === 'summary_best_pace') return true;
  return false;
}

function fmtPeak(value: number, unit: string, u: UnitsArg, kind: string): string {
  switch (unit) {
    case 'W':
      return `${Math.round(value).toLocaleString()} W`;
    case 'mps':
      return fmtSpeedUnits(value, u);
    case 'sec_per_m': {
      const mps = value > 0 ? 1 / value : 0;
      return isRunPaceKindForFeed(kind)
        ? fmtPaceUnits(mps, u)
        : fmtSpeedUnits(mps, u);
    }
    case 'sec':
      return `${value}s`;
    case 'm':
      return fmtDistanceUnits(value, u);
    case 'm_per_h':
      // VAM — climbing rate. Imperial rides think in ft/h.
      return u === 'imperial'
        ? `${Math.round(value * 3.28084).toLocaleString()} ft/h`
        : `${Math.round(value).toLocaleString()} m/h`;
    default:
      return `${value}`;
  }
}
